import { z } from "zod";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, ScanLine, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { CameraScanner, type OCRResult } from "@/components/CameraScanner";
import { toast } from "sonner";
import { useWorkflowRunner } from "../runnerContext";
import { useAppliance } from "@/hooks/useAppliance";
import { HarvestWorkflowHeader } from "@/components/harvest/HarvestWorkflowHeader";
import type { ModuleProps } from "../types";

export const workflowCompleteConfig = z.object({
  passportLink: z.boolean().default(true),
  showCompletedMeta: z.boolean().default(true),
  showTotalDuration: z.boolean().default(true),
  /** Optional breadcrumb. All entries before the last render as checked;
   *  the last entry renders highlighted (= "Complete"). */
  steps: z.array(z.string()).optional(),
  sections: z
    .array(
      z.object({
        title: z.string(),
        moduleKey: z.string(),
        fields: z.array(z.string()).optional(),
        renderer: z
          .enum(["components", "grade", "list", "json", "harvested_parts"])
          .default("json"),
      }),
    )
    .default([]),
});
export type WorkflowCompleteConfig = z.infer<typeof workflowCompleteConfig>;

interface WorkOrderMeta {
  status: string | null;
  created_at: string | null;
  completed_at: string | null;
  data: { modules?: Record<string, any> } | null;
  user_id: string | null;
  completedByEmail?: string | null;
}

function fmtDuration(ms: number) {
  if (!ms || ms < 0) return "—";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function WorkflowCompleteModule({
  workOrderId,
  applianceId,
  config,
}: ModuleProps<WorkflowCompleteConfig>) {
  const navigate = useNavigate();
  const runner = useWorkflowRunner();
  const isHarvesting = runner?.workflowKey === "harvesting";
  const { appliance: harvestAppliance } = useAppliance(isHarvesting ? applianceId : undefined);
  const [scannerOpen, setScannerOpen] = useState(false);

  const { data: wo } = useQuery({
    queryKey: ["workflow_complete_wo", workOrderId],
    enabled: !!workOrderId,
    queryFn: async (): Promise<WorkOrderMeta | null> => {
      const { data } = await (supabase as any)
        .from("work_orders")
        .select("status, created_at, completed_at, data, user_id")
        .eq("id", workOrderId!)
        .maybeSingle();
      return data as WorkOrderMeta | null;
    },
  });

  const modules = (wo?.data?.modules ?? {}) as Record<string, any>;
  const totalDurationMs = useMemo(() => {
    if (!wo?.created_at || !wo?.completed_at) return 0;
    return new Date(wo.completed_at).getTime() - new Date(wo.created_at).getTime();
  }, [wo?.created_at, wo?.completed_at]);

  const handleScanCapture = (typeShieldUrl: string, ocrResult?: OCRResult) => {
    if (ocrResult?.modelNumber) {
      navigate("/search", { state: { scannedTypeShieldUrl: typeShieldUrl, scannedResult: ocrResult } });
    } else {
      toast.warning("Could not read a model number from the type shield");
    }
  };



  return (
    <>
    {isHarvesting && (
      <HarvestWorkflowHeader
        applianceId={applianceId}
        workOrderId={workOrderId}
        brand={harvestAppliance?.brand}
        model={harvestAppliance?.modelNumber}
        serial={harvestAppliance?.serialNumber}
        category={harvestAppliance?.category}
        currentStep={5}
        stepTitle="Work Order Completed"
        stepHint="All parts are now visible in Inventory."
      />
    )}

    <div className={isHarvesting ? "max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-4" : "space-y-4"}>




      <Card className="p-6 flex items-start gap-4 bg-green-50 border-green-200">
        <CheckCircle2 className="w-8 h-8 text-green-600 shrink-0" />
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-green-900">Workflow complete</h2>
          <p className="text-sm text-green-800/80 mt-1">
            This work order is finished. Earlier steps are locked to preserve the record.
          </p>
        </div>
      </Card>

      {config.showCompletedMeta && wo?.completed_at && (
        <Card className="p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Completed</p>
              <p className="font-medium">{new Date(wo.completed_at).toLocaleString()}</p>
            </div>
            {config.showTotalDuration && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total time</p>
                <p className="font-medium">{fmtDuration(totalDurationMs)}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {config.sections.map((section, i) => (
        <SectionCard
          key={`${section.moduleKey}-${i}`}
          title={section.title}
          renderer={section.renderer ?? "json"}
          state={modules[section.moduleKey]}
          fields={section.fields}
          applianceId={applianceId}
        />
      ))}

      <div className="flex flex-wrap gap-3 pt-2">
        {config.passportLink && !isHarvesting && (
          <Button onClick={() => navigate(`/passport/${applianceId}`)} className="gap-2">
            <ExternalLink className="w-4 h-4" />
            View Digital Product Passport
          </Button>
        )}
        <Button onClick={() => setScannerOpen(true)} className="gap-2">
          <ScanLine className="w-4 h-4" />
          Scan New Device
        </Button>
        <Button variant="outline" onClick={() => navigate("/work-orders")} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Work Orders
        </Button>
      </div>
    </div>

    <CameraScanner
      open={scannerOpen}
      onOpenChange={setScannerOpen}
      onCapture={handleScanCapture}
    />
    </>

  );
}



function SectionCard({
  title,
  renderer,
  state,
  fields,
  applianceId,
}: {
  title: string;
  renderer: "components" | "grade" | "list" | "json" | "harvested_parts";
  state: any;
  fields?: string[];
  applianceId?: string;
}) {
  return (
    <Card className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h3>
      {state == null && renderer !== "harvested_parts" ? (
        <p className="text-sm text-muted-foreground italic">No data captured.</p>
      ) : (
        <RendererSwitch renderer={renderer} state={state} fields={fields} applianceId={applianceId} />
      )}
    </Card>
  );
}

function RendererSwitch({
  renderer,
  state,
  fields,
  applianceId,
}: {
  renderer: "components" | "grade" | "list" | "json" | "harvested_parts";
  state: any;
  fields?: string[];
  applianceId?: string;
}) {
  if (renderer === "components") return <ComponentsRenderer state={state} />;
  if (renderer === "grade") return <GradeRenderer state={state} />;
  if (renderer === "list") return <ListRenderer state={state} fields={fields} />;
  if (renderer === "harvested_parts") return <HarvestedPartsRenderer state={state} applianceId={applianceId} />;
  return <JsonRenderer state={state} fields={fields} />;
}

function HarvestedPartsRenderer({ state, applianceId }: { state: any; applianceId?: string }) {
  const sparePartIds: string[] = Array.isArray(state?.sparePartIds) ? state.sparePartIds : [];

  const { data: appliance, isLoading: applianceLoading, isError: applianceError, refetch: refetchAppliance } =
    useQuery({
      queryKey: ["wfc-appliance-serial", applianceId],
      enabled: !!applianceId,
      queryFn: async () => {
        const { data, error } = await (supabase as any)
          .from("appliances")
          .select("id, serial_number, brand, model_number")
          .eq("id", applianceId)
          .maybeSingle();
        if (error) throw error;
        return data as
          | { id: string; serial_number: string | null; brand: string | null; model_number: string | null }
          | null;
      },
    });

  const { data: parts } = useQuery({
    queryKey: ["wfc-spare-parts", sparePartIds.join(",")],
    enabled: sparePartIds.length > 0,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("spare_parts")
        .select("id, name, serial_no, bin_id, status, extraction_status")
        .in("id", sparePartIds);
      return (data ?? []) as Array<{ id: string; name: string; serial_no: string; bin_id: string | null; status: string; extraction_status: string | null }>;
    },
  });

  // Verification: serial is "confirmed" only after the DB has responded and
  // returned a non-empty serial_number for the appliance row.
  const verification: "loading" | "missing-id" | "error" | "not-found" | "missing-serial" | "verified" =
    !applianceId
      ? "missing-id"
      : applianceLoading
        ? "loading"
        : applianceError
          ? "error"
          : !appliance
            ? "not-found"
            : !appliance.serial_number || appliance.serial_number.trim() === ""
              ? "missing-serial"
              : "verified";

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Source appliance serial</p>

        {verification === "verified" && (
          <>
            <p className="font-mono text-sm mt-1 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" aria-hidden="true" />
              <span>{appliance!.serial_number}</span>
            </p>
            {(appliance?.brand || appliance?.model_number) && (
              <p className="text-xs text-muted-foreground mt-1">
                {[appliance?.brand, appliance?.model_number].filter(Boolean).join(" · ")}
              </p>
            )}
            <p className="text-[11px] text-green-700 mt-1">Verified against database.</p>
          </>
        )}

        {verification === "loading" && (
          <p className="text-sm text-muted-foreground italic mt-1">Verifying serial in database…</p>
        )}

        {verification === "missing-id" && (
          <p className="text-sm text-amber-700 mt-1">No appliance reference on this work order — cannot verify.</p>
        )}

        {verification === "not-found" && (
          <p className="text-sm text-amber-700 mt-1">
            Appliance row not found in the database. Serial cannot be shown.
          </p>
        )}

        {verification === "error" && (
          <div className="mt-1 space-y-2">
            <p className="text-sm text-destructive">Could not reach the database to verify the serial.</p>
            <button
              type="button"
              onClick={() => refetchAppliance()}
              className="text-xs underline text-muted-foreground hover:text-foreground"
            >
              Retry verification
            </button>
          </div>
        )}

        {verification === "missing-serial" && (
          <div className="mt-1 space-y-1">
            <p className="text-sm text-amber-700">
              No serial number is recorded on the source appliance. Add it on the appliance detail page so it can
              appear here.
            </p>
            {(appliance?.brand || appliance?.model_number) && (
              <p className="text-xs text-muted-foreground">
                {[appliance?.brand, appliance?.model_number].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
        )}
      </div>

      {sparePartIds.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No spare parts recorded.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Harvested spare parts ({sparePartIds.length})
          </p>
          <ul className="divide-y divide-border rounded-md border border-border">
            {(parts ?? []).map((p) => {
              const isDamaged = p.extraction_status === "damaged" || p.status === "damaged";
              return (
                <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="font-medium truncate flex-1 min-w-0">{p.name}</span>
                  {isDamaged && (
                    <Badge variant="destructive" className="shrink-0 text-xs">
                      Damaged
                    </Badge>
                  )}
                  <span className="font-mono text-xs text-muted-foreground shrink-0">{p.serial_no}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function ComponentsRenderer({ state }: { state: any }) {
  const selectedIds: string[] = Array.isArray(state?.selectedComponentIds) ? state.selectedComponentIds : [];
  const custom: Array<{ name?: string; weight_kg?: number }> = Array.isArray(state?.customComponents)
    ? state.customComponents
    : [];

  const { data: rows } = useQuery({
    queryKey: ["wfc-components", selectedIds.join(",")],
    enabled: selectedIds.length > 0,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("harvest_components")
        .select("id, name, default_weight_kg")
        .in("id", selectedIds);
      return (data ?? []) as Array<{ id: string; name: string; default_weight_kg: number | null }>;
    },
  });

  if (selectedIds.length === 0 && custom.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No components selected.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {(rows ?? []).map((r) => (
        <Badge key={r.id} variant="secondary" className="text-xs">
          {r.name}
          {r.default_weight_kg ? ` · ${r.default_weight_kg} kg` : ""}
        </Badge>
      ))}
      {custom.map((c, i) => (
        <Badge key={`custom-${i}`} variant="outline" className="text-xs">
          {c.name || "Custom"}
          {c.weight_kg ? ` · ${c.weight_kg} kg` : ""}
        </Badge>
      ))}
    </div>
  );
}

function GradeRenderer({ state }: { state: any }) {
  const grade = state?.grade ?? state?.finalGrade;
  if (!grade) return <p className="text-sm text-muted-foreground italic">No grade assigned.</p>;
  const color =
    grade === "A" ? "bg-green-100 text-green-700"
      : grade === "B" ? "bg-yellow-100 text-yellow-700"
      : "bg-orange-100 text-orange-700";
  return (
    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg font-bold text-2xl ${color}`}>
      {grade}
    </div>
  );
}

function ListRenderer({ state, fields }: { state: any; fields?: string[] }) {
  const items: string[] = [];
  const obj = state && typeof state === "object" ? state : {};
  const keys = fields && fields.length ? fields : Object.keys(obj);
  for (const k of keys) {
    const v = obj[k];
    if (v == null) continue;
    if (Array.isArray(v)) v.forEach((x) => items.push(String(x)));
    else items.push(`${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`);
  }
  if (!items.length) return <p className="text-sm text-muted-foreground italic">Nothing to show.</p>;
  return (
    <ul className="text-sm list-disc pl-5 space-y-1">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function JsonRenderer({ state, fields }: { state: any; fields?: string[] }) {
  const view = fields && fields.length && state && typeof state === "object"
    ? Object.fromEntries(fields.filter((f) => f in state).map((f) => [f, state[f]]))
    : state;
  return (
    <pre className="text-xs bg-muted/40 rounded p-3 overflow-auto max-h-64">
      {JSON.stringify(view, null, 2)}
    </pre>
  );
}
